class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.focusFrame = null;
    this.focusTimer = null;
    this.focusSetupToken = 0;
    this.focusTrapActive = false;
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);
    this.onCloseClick = this.close.bind(this);
    this.onTransitionEnd = this.handleTransitionEnd.bind(this);
    this.onFocusTrapKeyDown = this.handleFocusTrapKeyDown.bind(this);
    this.onDocumentFocusIn = this.handleDocumentFocusIn.bind(this);

    if (!this.notification) return;

    this.notification.setAttribute('aria-hidden', 'true');
    this.notification.addEventListener('keyup', this.onKeyUp);
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.onCloseClick)
    );
  }

  disconnectedCallback() {
    if (!this.notification) return;

    this.cancelFocusSetup();
    this.deactivateFocusTrap();
    document.body.removeEventListener('click', this.onBodyClick);
    this.notification.removeEventListener('keyup', this.onKeyUp);
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.removeEventListener('click', this.onCloseClick)
    );
  }

  open() {
    if (!this.notification) return;

    this.cancelFocusSetup();
    this.notification.setAttribute('aria-hidden', 'false');
    this.notification.classList.add('animate', 'active');
    document.body.removeEventListener('click', this.onBodyClick);
    document.body.addEventListener('click', this.onBodyClick);

    this.scheduleFocusSetup();
    this.dispatchCartViewEvent();
  }

  scheduleFocusSetup() {
    const token = ++this.focusSetupToken;
    const duration = this.getTransitionDurationMs();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.notification.addEventListener('transitionend', this.onTransitionEnd);

    this.focusFrame = requestAnimationFrame(() => {
      this.focusFrame = null;

      if (reducedMotion || duration === 0) {
        this.setupFocusTrap(token);
        return;
      }

      this.focusTimer = window.setTimeout(() => {
        this.focusTimer = null;
        this.setupFocusTrap(token);
      }, duration + 80);
    });
  }

  handleTransitionEnd(event) {
    if (event.target !== this.notification) return;
    this.setupFocusTrap(this.focusSetupToken);
  }

  setupFocusTrap(token) {
    if (
      token !== this.focusSetupToken ||
      !this.notification.classList.contains('active') ||
      !this.isConnected
    ) {
      return;
    }

    this.cancelFocusSetup(false);
    this.activateFocusTrap();
    this.notification.focus({ preventScroll: true });
  }

  activateFocusTrap() {
    this.deactivateFocusTrap();
    removeTrapFocus();

    document.addEventListener('keydown', this.onFocusTrapKeyDown, true);
    document.addEventListener('focusin', this.onDocumentFocusIn, true);
    this.focusTrapActive = true;
  }

  deactivateFocusTrap() {
    if (!this.focusTrapActive) return;

    document.removeEventListener('keydown', this.onFocusTrapKeyDown, true);
    document.removeEventListener('focusin', this.onDocumentFocusIn, true);
    this.focusTrapActive = false;
  }

  getFocusableElements() {
    const selector = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'details > summary:first-of-type',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(',');

    return Array.from(this.notification.querySelectorAll(selector)).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;

      const styles = getComputedStyle(element);
      return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  }

  handleFocusTrapKeyDown(event) {
    if (event.key !== 'Tab' || !this.notification.classList.contains('active')) return;

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault();
      this.notification.focus({ preventScroll: true });
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;
    const focusIsOutside = !this.notification.contains(activeElement);

    if (event.shiftKey) {
      if (activeElement === first || activeElement === this.notification || focusIsOutside) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (activeElement === last || focusIsOutside) {
      event.preventDefault();
      first.focus();
    }
  }

  handleDocumentFocusIn(event) {
    if (!this.notification.classList.contains('active')) return;
    if (this.notification.contains(event.target)) return;

    const [first] = this.getFocusableElements();
    (first || this.notification).focus({ preventScroll: true });
  }

  cancelFocusSetup(invalidate = true) {
    if (invalidate) this.focusSetupToken += 1;

    if (this.focusFrame !== null) {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
    }

    if (this.focusTimer !== null) {
      clearTimeout(this.focusTimer);
      this.focusTimer = null;
    }

    this.notification?.removeEventListener('transitionend', this.onTransitionEnd);
  }

  getTransitionDurationMs() {
    const styles = getComputedStyle(this.notification);
    const durations = styles.transitionDuration.split(',').map(this.timeToMilliseconds);
    const delays = styles.transitionDelay.split(',').map(this.timeToMilliseconds);
    const count = Math.max(durations.length, delays.length);
    let longest = 0;

    for (let index = 0; index < count; index += 1) {
      const duration = durations[index % durations.length] || 0;
      const delay = delays[index % delays.length] || 0;
      longest = Math.max(longest, duration + delay);
    }

    return longest;
  }

  timeToMilliseconds(value) {
    const normalized = String(value).trim();
    const amount = Number.parseFloat(normalized);
    if (!Number.isFinite(amount)) return 0;
    return normalized.endsWith('ms') ? amount : amount * 1000;
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      if (!response.ok) return;

      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    if (!this.notification) return;

    this.cancelFocusSetup();
    this.deactivateFocusTrap();
    this.notification.classList.remove('active');
    this.notification.setAttribute('aria-hidden', 'true');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    if (!this.notification || !parsedState?.sections) return;

    this.cartItemKey = parsedState.key;

    const productTarget = document.getElementById('cart-notification-product');
    if (productTarget) productTarget.replaceChildren();

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const html = parsedState.sections[section.id];
      const content = this.getSectionInnerHTML(html, section.selector);

      if (!target || content === null) return;
      target.innerHTML = content;
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || html.trim() === '') return null;

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const selectedElement = documentFragment.querySelector(selector);

    return selectedElement?.innerHTML ?? null;
  }

  handleKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }

  handleBodyClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : this.activeElement;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
